"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { CheckCheck, FileSearch, Shield, Target, TrendingUp, Check } from "lucide-react";
const services = [{
  id: 1,
  title: "Intellectual Property Analysis",
  icon: TrendingUp,
  description: "Prior art technical evaluation | Patentability assessments | Invention harvesting and ideation | Technical documentation to support filings"
}, {
  id: 2,
  title: "Product & Technology Audit",
  icon: Shield,
  description: "Technology maturity and scalability assessments | Product performance evaluation | Risk identification and mitigation planning | Competitive landscape technical analysis"
}, {
  id: 3,
  title: "Manufacturing & Supply Chain Assessment",
  icon: FileSearch,
  description: "Evaluation of manufacturing readiness and scalability | Audit of suppliers, contract manufacturers, and critical dependencies | Assessment of capacity, lead times, and supply-chain risk exposure"
}, {
  id: 5,
  title: "Risk Identification & Mitigation",
  icon: Target,
  description: "Identification of technical, quality, and operational risks | Assessment of failure modes, yield risks, and cost drivers | Practical mitigation strategies and post-close recommendations"
}, {
  id: 6,
  title: "Technical Valuation Support",
  icon: CheckCheck,
  description: "Evaluation of technology value drivers and constraints | Assessment of scalability, lifecycle potential, and cost structure | Data-backed inputs supporting purchase price, earn-outs, and integration planning"
}];
const deliverables = [{
  id: 1,
  title: "Comprehensive Technical Assessment Report",
  description: "Detailed documentation of technology evaluation, product performance analysis, and technical capabilities with data-driven insights and recommendations"
}, {
  id: 2,
  title: "Risk Analysis & Mitigation Strategy",
  description: "Identification of critical technical, operational, and quality risks with actionable mitigation plans and post-acquisition integration recommendations"
}, {
  id: 3,
  title: "Valuation Analysis Tied to IP, Product & Manufacturing Capabilities",
  description: "Complete IP portfolio assessment including patentability analysis, prior art evaluation, freedom-to-operate considerations, and strategic IP recommendations"
}, {
  id: 4,
  title: "Post-Acquisition Integration and Optimization Recommendations",
  description: "Assessment of production readiness, scalability potential, supplier dependencies, capacity constraints, and supply chain risk exposure with remediation strategies"
}];
export default function OurDueDiligenceServices() {
  const {
    ref: servicesRef,
    isVisible: servicesVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  const {
    ref: deliverablesRef,
    isVisible: deliverablesVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  return <>
      <section ref={servicesRef} className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Our Due Diligence Services
            </h2>
            <p className="text-muted text-lg max-w-4xl mx-auto">
              Our experience spans materials, systems, manufacturing processes, and product performance — enabling confident decision-making in fast-moving or high-uncertainty environments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <Card key={service.id} data-index={i} className={`
                    group cursor-pointer border-0 rounded-lg shadow-soft
                    bg-background transition-all duration-[var(--duration-medium)]
                    ease-[var(--ease-out-back)]
                    hover:-translate-y-1 hover:shadow-medium
                    ${servicesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
                  `} style={{
              transitionDelay: servicesVisible ? `${i * 100}ms` : "0ms"
            }}>
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 p-3 rounded-md bg-secondary-light
                                      transition-colors duration-[var(--duration-medium)]
                                      group-hover:bg-accent-light">
                        <Icon className="w-6 h-6 text-secondary transition-colors
                                     duration-[var(--duration-medium)]
                                     group-hover:text-accent-dark" strokeWidth={2} />
                      </div>
                      <CardTitle className="text-xl font-semibold text-foreground">
                        {service.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 opacity-0 max-h-0 overflow-hidden
                                         transition-all duration-[var(--duration-medium)]
                                         ease-[var(--ease-out-back)]
                                         group-hover:opacity-100 group-hover:max-h-48">
                    <p className="text-muted text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </section>

      <section ref={deliverablesRef} className="py-16 px-4 sm:px-6 lg:px-8 bg-muted-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Deliverables
            </h2>
            <p className="text-muted text-lg max-w-4xl mx-auto">
              Our due diligence engagements produce actionable, detailed documentation to support confident investment decisions and successful post-close integration.
            </p>
          </div>

          <ul className="max-w-4xl mx-auto space-y-6">
            {deliverables.map((deliverable, i) => <li key={deliverable.id} data-index={i} className={`
                  flex items-start gap-4 p-6 rounded-lg shadow-soft bg-background
                  transition-all duration-[var(--duration-medium)]
                  ease-[var(--ease-out-back)]
                  ${deliverablesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
                `} style={{
            transitionDelay: deliverablesVisible ? `${i * 100}ms` : "0ms"
          }}>
                <div className="flex-shrink-0 p-3 rounded-md bg-accent-light">
                  <Check className="w-6 h-6 text-accent-dark" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {deliverable.title}
                  </h3>
                  
                </div>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}